import { MatchScoreState } from '@/components/molecules/ScoreCards/modals/MatchScorecardModal';
import { Match } from '@/entities';
import { GameScore } from '@/entities/score/GameScore';
import { GameStatus } from '@/entities/score/GameStatus';
import { RetireOrForfeitGameInfo } from '@/entities/score/RetireOrForfeitGameInfo';

export const calculateScoreAfterRetireOrForfeit = (
	awayTeamScore: number,
	homeTeamScore: number,
	retireOrForfeitGameInfo: RetireOrForfeitGameInfo,
	match: Match
) => {
	const side = retireOrForfeitGameInfo.side;
	const maxScore = Number(match[`scoreFormatGame${retireOrForfeitGameInfo.gameNumber}Max` as keyof Match]!);
	const winBy = Number(match[`scoreFormatGame${retireOrForfeitGameInfo.gameNumber}WinBy` as keyof Match]!);

	if (retireOrForfeitGameInfo.type === 'undo') {
		return {
			away: awayTeamScore,
			home: homeTeamScore
		};
	} else if (retireOrForfeitGameInfo.type === 'forfeit') {
		return {
			away: side === 'away' ? 0 : maxScore,
			home: side === 'home' ? 0 : maxScore
		};
	} else if (retireOrForfeitGameInfo.type === 'withdraw') {
		return {
			away: side === 'away' ? 0 : 0,
			home: side === 'home' ? 0 : 0
		};
	} else {
		const isDifferenceSmallerThanWinBy = maxScore - awayTeamScore < winBy;
		const isAwayTeamOnMaxScore = awayTeamScore >= maxScore;
		const isHomeTeamOnMaxScore = homeTeamScore >= maxScore;
		const shouldIncreaseWithWinBy = awayTeamScore === homeTeamScore && isDifferenceSmallerThanWinBy;
		if (shouldIncreaseWithWinBy) {
			return {
				away: side === 'away' ? awayTeamScore : awayTeamScore + winBy,
				home: side === 'home' ? homeTeamScore : homeTeamScore + winBy
			};
		} else if (isAwayTeamOnMaxScore && side === 'away') {
			return {
				away: awayTeamScore,
				home: awayTeamScore + winBy
			};
		} else if (isHomeTeamOnMaxScore && side === 'home') {
			return {
				away: homeTeamScore + winBy,
				home: homeTeamScore
			};
		}
		return {
			away: side === 'away' ? awayTeamScore : maxScore,
			home: side === 'home' ? homeTeamScore : maxScore
		};
	}
};

export const determineInitialState = (match: Match): MatchScoreState => {
	const numberOfGames = match.scoreFormatMaxGames ?? 0;

	return {
		games: Array.from({ length: numberOfGames }).map((_, i) => {
			const gameSuffix = ['One', 'Two', 'Three', 'Four', 'Five'][i];
			const gameStatus = `game${gameSuffix}Status` as keyof Match;
			const awayTeamScore = `teamTwoGame${gameSuffix}Score` as keyof Match;
			const homeTeamScore = `teamOneGame${gameSuffix}Score` as keyof Match;

			return {
				away: {
					isRetired: match[gameStatus] === GameStatus.RETIREMENT_TEAM_TWO_GAME_COMPLETED_TYPE,
					isForfeited: match[gameStatus] === GameStatus.FORFEIT_TEAM_TWO_GAME_COMPLETED_TYPE,
					isWithdrawn: match[gameStatus] === GameStatus.WITHDRAWAL_TEAM_TWO_GAME_COMPLETED_TYPE,
					isUndo: false,
					score: Number(match[awayTeamScore])
				},
				home: {
					isRetired: match[gameStatus] === GameStatus.RETIREMENT_TEAM_ONE_GAME_COMPLETED_TYPE,
					isForfeited: match[gameStatus] === GameStatus.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE,
					isWithdrawn: match[gameStatus] === GameStatus.WITHDRAWAL_TEAM_ONE_GAME_COMPLETED_TYPE,
					isUndo: false,
					score: Number(match[homeTeamScore])
				}
			};
		}),
		isModalOpen: true,
		retireOrForfeitGameInfo: null,
		isCancelModalOpen: false
	};
};

const getGameStatus = (game: GameScore | undefined): string => {
	if (game?.isRetired) return 'RET';
	if (game?.isForfeited) return 'FF';
	if (game?.isWithdrawn) return 'WD';
	if (game?.isUndo) return 'UNDO_FF';
	return '0';
};

const getTeamScores = (games: { away: GameScore; home: GameScore }[], team: 'home' | 'away') => {
	const scores = games.map((game) => game?.[team]?.score?.toString() ?? '0');
	const wins = games.filter((game) => game?.[team]?.score > game?.[team === 'home' ? 'away' : 'home']?.score).length;
	const statuses = games.map((game) => getGameStatus(game?.[team]) ?? '0');

	return {
		game1: scores[0] ?? '0',
		game2: scores[1] ?? '0',
		game3: scores[2] ?? '0',
		game4: scores[3] ?? '0',
		game5: scores[4] ?? '0',
		final: wins.toString(),
		gameOneStatus: statuses[0] ?? '0',
		gameTwoStatus: statuses[1] ?? '0',
		gameThreeStatus: statuses[2] ?? '0',
		gameFourStatus: statuses[3] ?? '0',
		gameFiveStatus: statuses[4] ?? '0'
	};
};

export const mapStateToMatchScores = (state: MatchScoreState) => {
	return {
		home: getTeamScores(state.games, 'home'),
		away: getTeamScores(state.games, 'away')
	};
};

export const shouldMarkGameAsWon = (gameStatus: GameStatus, awayTeamScore: number, homeTeamScore: number, side: 'away' | 'home'): boolean => {
	// If the game was forfeited, retired, or withdrawn by team one, away wins
	if (
		(gameStatus === GameStatus.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE ||
			gameStatus === GameStatus.RETIREMENT_TEAM_ONE_GAME_COMPLETED_TYPE ||
			gameStatus === GameStatus.WITHDRAWAL_TEAM_ONE_GAME_COMPLETED_TYPE) &&
		side === 'away'
	) {
		return true;
	}

	// If the game was forfeited, retired, or withdrawn by team two, home wins
	if (
		(gameStatus === GameStatus.FORFEIT_TEAM_TWO_GAME_COMPLETED_TYPE ||
			gameStatus === GameStatus.RETIREMENT_TEAM_TWO_GAME_COMPLETED_TYPE ||
			gameStatus === GameStatus.WITHDRAWAL_TEAM_TWO_GAME_COMPLETED_TYPE) &&
		side === 'home'
	) {
		return true;
	}

	// Regular win
	if (gameStatus === GameStatus.PLAYED_COMPLETED_TYPE) {
		if (side === 'away') return awayTeamScore > homeTeamScore;
		if (side === 'home') return homeTeamScore > awayTeamScore;
	}

	// In all other cases
	return false;
};

export const numberToGameStatus = (value?: number): GameStatus | undefined => {
	if (!value) return undefined;
	const status = value.toString() as GameStatus;

	if (Object.values(GameStatus).includes(status)) {
		return status;
	}

	return undefined;
};
